import React from 'react';
import { useTranslation } from 'react-i18next';

import Select, {
  IProps as ISelectProps,
  IValue,
  IValues,
} from './_module/Select';
import {
  ICurrencyVarsPartial,
  useCurrencyQuery,
} from '../../../graphql/query/currency/useCurrencyQuery';
import useLanguage from '../_hook/useLanguage';

interface IProps<TValue extends IValue | IValues> extends ISelectProps<TValue> {
  queryVariables?: ICurrencyVarsPartial;
  useShortLabel?: boolean;
  hideLabel?: boolean;
}

export default function SelectCurrency<TValue extends IValue | IValues>(
  props: IProps<TValue>
) {
  const { queryVariables, useShortLabel, hideLabel } = props;
  const { t } = useTranslation();
  const { getText } = useLanguage();
  const {
    creatable,
    disabled,
    label,
    onChange,
    placeholder,
    value,
    menuPosition,
    required,
    margin,
    error,
    fullWidth,
    helperText,
    isMulti,
  } = props;

  const { loading, data } = useCurrencyQuery(
    `
      _id
      iso3
      name {
        text
        la
      }
  `,
    {
      variables: {
        ...{
          filter: {
            status: true,
          },
          sort: {
            iso3: 1,
          },
        },
        ...(queryVariables || {}),
      },
      fetchPolicy: 'cache-first',
    }
  );
  const options =
    data && data.currencies
      ? data.currencies.items.map((currency) => ({
          label: `${currency.iso3} ${
            useShortLabel ? '' : `: ${getText(currency.name)}`
          }`,
          value: currency.iso3,
        }))
      : [];

  return (
    <Select<TValue>
      loading={loading}
      creatable={creatable}
      isClearable
      isMulti={isMulti}
      options={options}
      menuPosition={menuPosition}
      label={hideLabel ? undefined : label || t('currency')}
      required={required}
      margin={margin}
      error={error}
      disabled={disabled}
      fullWidth={fullWidth}
      helperText={helperText}
      value={value}
      onChange={onChange}
      placeholder={placeholder || t('please_select')}
    />
  );
}
